'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { toast } from 'sonner'

import { dateToString } from 'utils/date'
import { useMainContext } from 'utils/context'
import { torsCats } from 'config/const'
import { getResizedImg } from 'utils/media'
import { sendForm } from 'services/private'
import Button from './Button'
import ButtonAction from './ButtonAction'
import JudgesReveal from './JudgesReveal'

const CardsTorsPrivate = ({
  tors,
  title,
  showCTA = false,
  showPayment = false,
  showPendentPayment = false,
  handleChange,
  isPublic = false,
  setResetCount,
  showBin,
  sortInverted = false,
  showYear = false
}) => {
  const { setTorId, fedId, fedToken } = useMainContext()
  const [showDeleteCTA, setShowDeleteCTA] = useState([])
  const [isSafari, setIsSafari] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [openJudgesIds, setOpenJudgesIds] = useState(new Set())

  const toggleJudges = (id) => {
    setOpenJudgesIds((prev) => new Set(prev).add(id))
  }

  const deleteTor = async (torId) => {
    if (isDeleting) return

    setIsDeleting(true)
    const response = await sendForm({
      user: fedId,
      token: fedToken,
      formData: { idDelete: torId },
      endpoint: 'PRIVATE_TOR_DELETE'
    })
    if (response.resultado === 'OK') {
      setResetCount((prev) => prev + 1)
      toast.success("El torneig s'ha eliminat correctament")
    } else if (response.resultado === 'ERROR') {
      // setError(response.error_desc)
      toast.error('Error en eliminar el torneig')
    }
    setIsDeleting(false)
  }

  useEffect(() => {
    // todo: is here ok to launch the right class or maybe is too late?
    setIsSafari(
      /constructor/i.test(window.HTMLElement) ||
        (function (p) {
          return p.toString() === '[object SafariRemoteNotification]'
        })(
          !window['safari'] ||
            (typeof safari !== 'undefined' && safari.pushNotification)
        )
    )
  }, [])

  return (
    <>
      <div className="flex flex-row items-center justify-between mb-10">
        <h2 className="text-[30px] lg:text-[40px] font-alliance-medium tracking-tight text-blue">
          {title}
        </h2>
      </div>
      <ul
        role="list"
        className={`grid grid-cols-1 sm:grid-cols-2 gap-0 sm:gap-4 lg:flex lg:flex-wrap ${
          isPublic ? 'lg:gap-4' : 'lg:gap-8'
        }`}
      >
        {tors
          .sort(
            (a, b) =>
              new Date(sortInverted ? b.StartDate : a.StartDate) -
              new Date(sortInverted ? a.StartDate : b.StartDate)
          )
          .map((tor, idx) => {
            const bgColor = tor?.color || 'bg-sec-blue'
            let [torName, torPlace] = (tor.Name || tor.name).split(' - ')
            torPlace = torPlace || ''
            let date
            if (tor?.StartDate) {
              date = `${
                dateToString(tor.StartDate)[
                  showYear ? 'withYearMinimal' : 'dayMin'
                ]
              } - ${
                dateToString(tor.EndDate)[
                  showYear ? 'withYearMinimal' : 'dayMin'
                ]
              }`
            }
            let endToRegister
            if (tor?.registrationDate && tor?.P1Price && tor?.P2Price) {
              endToRegister = `Límit inscripció fins ${
                dateToString(tor.MaximumDate).full
              }`
            }
            const torInfo = torsCats.find(
              ({ id }) => tor.CategoryFilter.toLowerCase() === id
            )
            const link = `${torInfo.torUrl}/${tor.Year}/${tor.Slug}/informacio`
            const linkRegistrationTeam =
              tor?.Type === 'EQUIPOS'
                ? `${torInfo.torUrl}/${tor.Year}/${tor.Slug}/formulari-dinscripcio`
                : null
            const linkRegistration = `/privat/federat/competicio/inscripcio/${tor.Slug}`
            const poster = tor.Image1
              ? getResizedImg(tor.Image1)
              : `/assets/images/tors-posters/${
                  torInfo.poster || torInfo.keyword
                }.webp`
            let currentPlayerOwe =
              tor.P1Price && `${tor.idPayment1}` === fedId
                ? tor.P1Price - tor.discountP1
                : tor.P2Price && `${tor.idPayment2}` === fedId
                ? tor.P2Price - tor.discountP2
                : 0
            const otherPlayerOwe =
              tor.P1Price && `${tor.idPayment1}` !== fedId
                ? tor.P1Price - tor.discountP1
                : tor.P2Price && `${tor.idPayment2}` !== fedId
                ? tor.P2Price - tor.discountP2
                : 0

            return (
              <li
                key={idx}
                className={`mb-4 lg:mb-10 ${isSafari ? 'mr-6' : ''}`}
              >
                {/* MOBILE VIEW */}
                <div className="block lg:hidden">
                  <hr className="block sm:hidden" />
                  <div className="flex bg-white mt-4">
                    <div className="group">
                      <Link
                        href={link}
                        onClick={async () => {
                          await setTorId(tor.id)
                        }}
                      >
                        <div className="relative w-[138px] h-full">
                          <div
                            className={`absolute w-full h-full transition ease-in-out bg-opacity-0 group-hover:${bgColor} group-hover:bg-opacity-80 duration-300`}
                          >
                            <div className="grid h-full place-items-center transition ease-in-out opacity-0 group-hover:opacity-100 duration-300">
                              <div className="flex justify-center">
                                <p className="text-[20px] font-alliance-medium text-blue">
                                  Ves-hi
                                </p>
                                <img
                                  src="/assets/icons/arrow-circle-right.svg"
                                  alt=""
                                  className="pointer-events-none object-fill w-6 ml-2"
                                />
                              </div>
                            </div>
                          </div>
                          <div className="">
                            <img
                              className="object-cover w-[138px] h-full"
                              src={poster}
                              alt=""
                            />
                          </div>
                        </div>
                      </Link>
                    </div>
                    <div className="grid m-3 w-full">
                      <div key={tor.Campus} className="flex capitalize">
                        <div className="flex flex-1 flex-col justify-between">
                          <div className="flex-1 leading-tight">
                            <p className="font-alliance-medium text-[24px] text-gray-900 capitalize">
                              {torName.toLowerCase()}
                            </p>
                            <p className="font-alliance-light text-[24px] text-blue capitalize">
                              {torPlace.toLowerCase()}
                            </p>
                          </div>
                          <div className="flex justify-between mt-2 w-full">
                            <div className="">
                              <div className="flex-shrink-0">
                                <span className="sr-only">{tor.datetime}</span>
                              </div>
                              <div className="">
                                <p className="text-[14px] font-alliance-medium text-blue">
                                  {tor.torPlace}
                                </p>
                                <p className="flex space-x-1 text-[14px] font-alliance-medium text-blue">
                                  {date}
                                </p>
                                <p className="flex space-x-1 text-[14px] font-alliance-medium text-blue opacity-50">
                                  {endToRegister}
                                </p>
                              </div>
                            </div>
                          </div>
                          {tor.idDelete && showBin ? (
                            <button
                              className="w-8 h-8 bg-white hover:bg-sec-red rounded-full"
                              onClick={() => {
                                setShowDeleteCTA((prev) =>
                                  showDeleteCTA.includes(tor.idInscripcion)
                                    ? prev.filter(
                                        (i) => i !== tor.idInscripcion
                                      )
                                    : [...prev, tor.idInscripcion]
                                )
                              }}
                            >
                              <img
                                className={`object-cover p-${
                                  showDeleteCTA.includes(tor.idInscripcion)
                                    ? '2'
                                    : '1'
                                }`}
                                src={`/assets/icons/${
                                  showDeleteCTA.includes(tor.idInscripcion)
                                    ? 'cross'
                                    : 'bin'
                                }.svg`}
                                alt=""
                              />
                            </button>
                          ) : null}
                        </div>
                        {showCTA ? (
                          <div className="">
                            <Link href={linkRegistration}>
                              <img
                                src={`/assets/icons/arrow-circle-right.svg`}
                                alt="icon"
                                className="w-8"
                              />
                            </Link>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  {showCTA ? (
                    <div className="mt-6">
                      {!tor.inscriptionClosed ? (
                        <Button
                          link={linkRegistrationTeam || linkRegistration}
                          text="INSCRIU-TE"
                          size="small"
                        />
                      ) : (
                        <Button
                          link={null}
                          text="INSCRIPCIÓ TANCADA"
                          size="small"
                          color="grey"
                        />
                      )}
                    </div>
                  ) : null}
                  {showDeleteCTA.includes(tor.idInscripcion) ? (
                    <div className="mt-6">
                      <ButtonAction
                        text="CONFIRMAR CANCEL·LACIÓ"
                        size="small"
                        color="sec-red"
                        textColor="white"
                        onClick={() => {
                          deleteTor(tor.idDelete)
                        }}
                        loading={isDeleting}
                        lightLoading
                      />
                    </div>
                  ) : (
                    <>
                      {showPayment ? (
                        <div className="mt-6">
                          {currentPlayerOwe ? (
                            <ButtonAction
                              name="idPayment1"
                              inscriptionId={tor.idInscripcion}
                              price={currentPlayerOwe}
                              payOptions={fedId}
                              text="PAGAR LA MEVA INSCRIPCIÓ"
                              size="small"
                              handleChange={handleChange}
                              className="cursor-pointer mb-4"
                            />
                          ) : null}
                          {!currentPlayerOwe && otherPlayerOwe ? (
                            <ButtonAction
                              name="idPayment2"
                              inscriptionId={tor.idInscripcion}
                              price={otherPlayerOwe}
                              payOptions={
                                `${tor.idPayment1}` !== fedId
                                  ? tor.idPayment1
                                  : tor.idPayment2
                              }
                              text="PAGAR INSCRIPCIÓ COMPANY"
                              size="small"
                              handleChange={handleChange}
                              className="cursor-pointer mb-4"
                            />
                          ) : null}
                          {currentPlayerOwe && otherPlayerOwe ? (
                            <ButtonAction
                              name="idPaymentAll"
                              inscriptionId={tor.idInscripcion}
                              price={currentPlayerOwe + otherPlayerOwe}
                              payOptions={tor.idPaymentAll}
                              text="PAGAR AMB DUES INSCRIPCIONS"
                              size="small"
                              handleChange={handleChange}
                              className="mt-3 cursor-pointer mb-4"
                            />
                          ) : null}
                        </div>
                      ) : null}
                      {showPendentPayment && (tor?.P1Price || tor?.P2Price) ? (
                        <div className="mt-6">
                          <Button
                            link="/privat/federat/tramits/pagament-tornejos"
                            text="PENDENT DE PAGAMENT"
                            size="small"
                          />
                        </div>
                      ) : null}
                    </>
                  )}
                  {tor.Judges?.length ? (
                    <JudgesReveal judges={tor.Judges} />
                  ) : null}
                </div>
                {/* DESKTOP VIEW */}
                <div className="hidden lg:block max-w-[300px]">
                  <div className="">
                    <div className="relative h-full">
                      <div
                        className={`group absolute w-full h-full place-items-center transition ease-in-out opacity-0 hover:${bgColor} opacity-80 duration-300`}
                      >
                        <Link
                          href={link}
                          onClick={async () => {
                            await setTorId(tor.id)
                          }}
                        >
                          <div className="w-full h-full flex justify-center items-center opacity-0 group-hover:opacity-100">
                            <p className="text-[40px] font-alliance-medium text-blue">
                              Ves-hi
                            </p>
                            <img
                              src="/assets/icons/arrow-circle-right.svg"
                              alt=""
                              className="pointer-events-none object-fill w-12 ml-5"
                            />
                          </div>
                        </Link>
                      </div>
                      {tor.idDelete && showBin ? (
                        <button
                          className="absolute w-8 h-8 bg-white hover:bg-sec-red rounded-full top-2 right-2"
                          onClick={() => {
                            setShowDeleteCTA((prev) =>
                              showDeleteCTA.includes(tor.idInscripcion)
                                ? prev.filter((i) => i !== tor.idInscripcion)
                                : [...prev, tor.idInscripcion]
                            )
                          }}
                        >
                          <img
                            className={`object-cover p-${
                              showDeleteCTA.includes(tor.idInscripcion)
                                ? '2'
                                : '1'
                            }`}
                            src={`/assets/icons/${
                              showDeleteCTA.includes(tor.idInscripcion)
                                ? 'cross'
                                : 'bin'
                            }.svg`}
                            alt=""
                          />
                        </button>
                      ) : null}
                      <div className="">
                        <img className="object-cover" src={poster} alt="" />
                      </div>
                    </div>
                  </div>
                  {showCTA ? (
                    <div className="mt-6">
                      {!tor.inscriptionClosed ? (
                        <Button
                          link={linkRegistrationTeam || linkRegistration}
                          text="INSCRIU-TE"
                          size="small"
                        />
                      ) : (
                        <Button
                          link={null}
                          text="INSCRIPCIÓ TANCADA"
                          size="small"
                          color="grey"
                        />
                      )}
                    </div>
                  ) : null}
                  {showDeleteCTA.includes(tor.idInscripcion) ? (
                    <div className="mt-6">
                      <ButtonAction
                        text="CONFIRMAR CANCEL·LACIÓ"
                        size="small"
                        color="sec-red"
                        textColor="white"
                        onClick={() => {
                          deleteTor(tor.idDelete)
                        }}
                        loading={isDeleting}
                        lightLoading
                      />
                    </div>
                  ) : (
                    <>
                      {showPayment ? (
                        <div className="mt-6">
                          {currentPlayerOwe ? (
                            <ButtonAction
                              name="idPayment1"
                              inscriptionId={tor.idInscripcion}
                              price={currentPlayerOwe}
                              payOptions={fedId}
                              text="PAGAR LA MEVA INSCRIPCIÓ"
                              size="small"
                              handleChange={handleChange}
                              className="cursor-pointer"
                            />
                          ) : null}
                          {!currentPlayerOwe && otherPlayerOwe ? (
                            <ButtonAction
                              name="idPayment2"
                              inscriptionId={tor.idInscripcion}
                              price={otherPlayerOwe}
                              payOptions={
                                `${tor.idPayment1}` !== fedId
                                  ? tor.idPayment1
                                  : tor.idPayment2
                              }
                              text="PAGAR INSCRIPCIÓ COMPANY"
                              size="small"
                              handleChange={handleChange}
                              className="cursor-pointer"
                            />
                          ) : null}
                          {currentPlayerOwe && otherPlayerOwe ? (
                            <ButtonAction
                              name="idPaymentAll"
                              inscriptionId={tor.idInscripcion}
                              price={currentPlayerOwe + otherPlayerOwe}
                              payOptions={tor.idPaymentAll}
                              text="PAGAR AMB DUES INSCRIPCIONS"
                              size="small"
                              handleChange={handleChange}
                              className="mt-3 cursor-pointer"
                            />
                          ) : null}
                        </div>
                      ) : null}
                      {showPendentPayment && (tor?.P1Price || tor?.P2Price) ? (
                        <div className="mt-6">
                          <Button
                            link="/privat/federat/tramits/pagament-tornejos"
                            text="PENDENT DE PAGAMENT"
                            size="small"
                          />
                        </div>
                      ) : null}
                      <div key={tor.Campus} className="flex flex-col">
                        <div className="flex flex-1 flex-col justify-between">
                          <div className="flex-1 leading-tight min-h-[100px]">
                            <p className="mt-6 font-alliance-medium text-[30px] text-gray-900">
                              {torName}
                            </p>
                            {/* {!isPublic && ( */}
                            <p className="font-alliance-light text-[30px] text-blue capitalize">
                              {torPlace.toLowerCase()}
                            </p>
                            {/* )} */}
                          </div>
                          <div className="flex items-center mt-6">
                            <div className="flex-shrink-0">
                              <span className="sr-only">{tor.datetime}</span>
                            </div>
                            <div className="">
                              <p className="text-[18px] font-alliance-medium text-blue">
                                {tor.torPlace}
                              </p>
                              <p className="flex space-x-1 text-[18px] font-alliance-medium text-blue">
                                {date}
                              </p>
                              <p className="flex space-x-1 text-[14px] font-alliance-medium text-blue opacity-50">
                                {endToRegister}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      {tor.Judges?.length ? (
                        <JudgesReveal judges={tor.Judges} />
                      ) : null}
                    </>
                  )}
                </div>
              </li>
            )
          })}
      </ul>
    </>
  )
}

export default CardsTorsPrivate
